<script setup lang="ts">
import { computed, type Component, type PropType } from 'vue';
import {
  IconBook,
  IconBusiness,
  IconCalendar,
  IconDocumentLock,
  IconDocumentText,
  IconPlayCircle,
  IconSign,
} from '@/components/design/icons';
import type { ChatLinkKind } from '@/utils/chat/gaiaLinks';
import { gaiaAccent, gaiaFontWeight } from '../../themes/tokens';

const props = defineProps({
  kind: {
    type: String as PropType<ChatLinkKind>,
    required: true,
  },
  /** App path the link opens. */
  path: {
    type: String,
    required: true,
  },
  /** The name the agent wrote as the link text. */
  name: {
    type: String,
    required: true,
  },
});

const emit = defineEmits<{
  /** A plain click — the host pushes the path; this component has no router. */
  open: [path: string];
}>();

const META: Record<ChatLinkKind, { icon: Component; label: string }> = {
  contract: { icon: IconDocumentLock, label: 'contract' },
  contractFile: { icon: IconDocumentText, label: 'contract file' },
  matter: { icon: IconBusiness, label: 'matter' },
  signingRequest: { icon: IconSign, label: 'signing request' },
  playbook: { icon: IconBook, label: 'playbook' },
  playbookRun: { icon: IconPlayCircle, label: 'playbook run' },
  calendar: { icon: IconCalendar, label: 'calendar' },
};

const meta = computed(() => META[props.kind]);

/**
 * Longer names are cut to this many characters. By count, not by CSS width: a
 * width cap needs `inline-block` + `overflow: hidden`, which drops the link off
 * the text baseline — the misalignment this link exists to avoid.
 */
const MAX_NAME_LENGTH = 60;

const truncated = computed(() => props.name.length > MAX_NAME_LENGTH);
const shownName = computed(() =>
  truncated.value ? `${props.name.slice(0, MAX_NAME_LENGTH - 1).trimEnd()}…` : props.name
);

/**
 * A real link, so Cmd/Ctrl/Shift/middle-click keep the browser's own new-tab
 * and new-window behaviour; only a plain click is taken over for SPA navigation.
 */
function onClick(event: MouseEvent): void {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)
    return;
  event.preventDefault();
  emit('open', props.path);
}

const accent = gaiaAccent.strong;
const iconColor = gaiaAccent.soft;
const underline = gaiaAccent.border;
const tint = gaiaAccent.surface;
const ring = gaiaAccent.softRing;
const weight = String(gaiaFontWeight.medium);
</script>

<template>
  <!-- The full name on hover, but only when it was shortened; otherwise the
       browser's own title says what the link opens. -->
  <n-tooltip :disabled="!truncated" :style="{ maxWidth: '360px' }">
    <template #trigger>
      <a
        class="ref"
        :href="props.path"
        :title="truncated ? undefined : `Open ${meta.label}`"
        :aria-label="truncated ? `Open ${meta.label}: ${props.name}` : undefined"
        @click="onClick"
      >
        <n-icon class="ref__icon" :component="meta.icon" :size="14" />{{ shownName }}
      </a>
    </template>
    {{ props.name }}
  </n-tooltip>
</template>

<style scoped>
/* An inline link, not a chip: it stays on the text baseline and wraps with the
   sentence, so a line holding a reference is as tall as one without. Naive has
   no inline-link primitive and the hover/focus states need pseudo-classes. The
   soft underline is the at-rest "this is clickable" cue — colour alone isn't
   enough for colour-blind readers. */
.ref {
  color: v-bind(accent);
  font-weight: v-bind(weight);
  text-decoration: underline;
  text-decoration-color: v-bind(underline);
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
  border-radius: 4px;
  /* Hover tint hugs the text; `clone` keeps it on every line when a long name wraps. */
  padding: 1px 2px;
  margin: 0 -2px;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
  transition:
    background-color 0.15s,
    text-decoration-color 0.15s;
}

.ref:hover {
  background: v-bind(tint);
  text-decoration-color: currentColor;
}

.ref:focus-visible {
  outline: 2px solid v-bind(ring);
  outline-offset: 1px;
}

/* Sits on the text line like a glyph rather than centring in a box. */
.ref__icon {
  margin-right: 3px;
  vertical-align: -2px;
  color: v-bind(iconColor);
  transition: color 0.15s;
}

.ref:hover .ref__icon {
  color: v-bind(accent);
}

@media (prefers-reduced-motion: reduce) {
  .ref,
  .ref__icon {
    transition: none;
  }
}
</style>
